import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { notificationsFeed } from '@/mocks/notifications';
import { laundryProfile, dashboardNavCounts } from '@/mocks/dashboard';

interface DashboardTopbarProps {
  title: string;
  onOpenMobileSidebar: () => void;
}

const toneClasses: Record<string, string> = {
  info: 'bg-info-100 text-info-700',
  primary: 'bg-secondary-100 text-secondary-700',
  warning: 'bg-warning-100 text-warning-700',
  accent: 'bg-accent-100 text-accent-700',
  success: 'bg-success-100 text-success-700',
};

export default function DashboardTopbar({ title, onOpenMobileSidebar }: DashboardTopbarProps) {
  const navigate = useNavigate();
  const [bellOpen, setBellOpen] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);

  return (
    <header className="sticky top-0 z-30 h-16 border-b border-background-200 bg-white">
      <div className="flex h-full items-center gap-3 px-4 md:px-6">
        <button
          type="button"
          onClick={onOpenMobileSidebar}
          className="flex h-9 w-9 items-center justify-center rounded-md text-foreground-700 transition-colors hover:bg-background-100 lg:hidden"
          aria-label="فتح القائمة"
        >
          <i className="ri-menu-2-line text-xl" aria-hidden="true"></i>
        </button>

        <h1 className="font-bold text-[17px] text-foreground-950 md:text-lg">{title}</h1>

        <div className="relative ms-auto hidden md:block">
          <span className="pointer-events-none absolute right-3 top-1/2 flex h-5 w-5 -translate-y-1/2 items-center justify-center text-foreground-400">
            <i className="ri-search-line text-[17px] leading-none" aria-hidden="true"></i>
          </span>
          <input
            type="search"
            placeholder="ابحث عن طلب، عميل، أو قطعة..."
            className="h-10 w-[260px] rounded-field border border-background-300 bg-background-50 pe-3 ps-10 text-sm text-foreground-900 placeholder:text-foreground-400 focus:border-secondary-400 focus:outline-none focus:ring-2 focus:ring-secondary-100 xl:w-[320px]"
          />
        </div>

        <div className="ms-auto flex items-center gap-1 md:ms-0">
          <div className="relative">
            <button
              type="button"
              onClick={() => {
                setBellOpen((prev) => !prev);
                setMenuOpen(false);
              }}
              className="relative flex h-10 w-10 items-center justify-center rounded-md text-foreground-700 transition-colors hover:bg-background-100"
              aria-label="الإشعارات"
            >
              <i className="ri-notification-3-line text-xl" aria-hidden="true"></i>
              {dashboardNavCounts.notifications > 0 && (
                <span className="absolute right-1.5 top-1.5 flex h-4 min-w-4 items-center justify-center rounded-full bg-danger-500 px-1 text-[10px] font-semibold text-white">
                  {dashboardNavCounts.notifications}
                </span>
              )}
            </button>

            {bellOpen && (
              <>
                <div
                  className="fixed inset-0 z-10"
                  onClick={() => setBellOpen(false)}
                  aria-hidden="true"
                ></div>
                <div className="dc-fade-up absolute left-0 z-20 mt-2 w-[330px] rounded-card border border-background-200 bg-white p-2 shadow-soft">
                  <div className="flex items-center justify-between px-2 py-1.5">
                    <span className="font-semibold text-sm text-foreground-900">الإشعارات</span>
                    <span className="rounded-full bg-secondary-100 px-2 py-0.5 text-[11px] font-medium text-secondary-700">
                      {dashboardNavCounts.notifications} غير مقروء
                    </span>
                  </div>
                  <ul className="max-h-[300px] overflow-y-auto">
                    {notificationsFeed.map((item) => (
                      <li key={item.id}>
                        <button
                          type="button"
                          onClick={() => {
                            setBellOpen(false);
                            navigate('/notifications');
                          }}
                          className="flex w-full items-start gap-3 rounded-lg px-2 py-2.5 text-right transition-colors hover:bg-background-50"
                        >
                          <span
                            className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-full ${toneClasses[item.tone] || toneClasses.info}`}
                          >
                            <i className={`${item.icon} text-base`} aria-hidden="true"></i>
                          </span>
                          <span className="min-w-0 flex-1">
                            <span className="flex items-center gap-2">
                              <span className="truncate text-[13px] font-medium text-foreground-900">
                                {item.title}
                              </span>
                              {item.unread && (
                                <span className="h-2 w-2 shrink-0 rounded-full bg-danger-500"></span>
                              )}
                            </span>
                            <span className="mt-0.5 block truncate text-[12px] text-foreground-500">
                              {item.body}
                            </span>
                            <span className="mt-0.5 block text-[11px] text-foreground-400">
                              {item.time}
                            </span>
                          </span>
                        </button>
                      </li>
                    ))}
                  </ul>
                  <button
                    type="button"
                    onClick={() => {
                      setBellOpen(false);
                      navigate('/notifications');
                    }}
                    className="mt-1 w-full rounded-lg py-2 text-center text-[13px] font-medium text-secondary-600 transition-colors hover:bg-secondary-50"
                  >
                    عرض كل الإشعارات
                  </button>
                </div>
              </>
            )}
          </div>

          <div className="relative">
            <button
              type="button"
              onClick={() => {
                setMenuOpen((prev) => !prev);
                setBellOpen(false);
              }}
              className="flex items-center gap-2 rounded-full p-1 pe-2 transition-colors hover:bg-background-100"
              aria-label="حساب المغسلة"
            >
              <span className="h-9 w-9 overflow-hidden rounded-full border border-background-200">
                <img
                  src={laundryProfile.logo}
                  alt={laundryProfile.name}
                  className="h-full w-full object-cover"
                />
              </span>
              <i className="ri-arrow-down-s-line text-base text-foreground-500" aria-hidden="true"></i>
            </button>

            {menuOpen && (
              <>
                <div
                  className="fixed inset-0 z-10"
                  onClick={() => setMenuOpen(false)}
                  aria-hidden="true"
                ></div>
                <div className="dc-fade-up absolute left-0 z-20 mt-2 w-[230px] rounded-card border border-background-200 bg-white p-2 shadow-soft">
                  <div className="border-b border-background-100 px-3 py-2">
                    <p className="truncate text-[13px] font-semibold text-foreground-900">
                      {laundryProfile.name}
                    </p>
                    <p className="truncate text-[12px] text-foreground-500">
                      {laundryProfile.city}
                    </p>
                  </div>
                  <button
                    type="button"
                    onClick={() => {
                      setMenuOpen(false);
                      navigate('/settings');
                    }}
                    className="mt-1 flex w-full items-center gap-2 rounded-lg px-3 py-2.5 text-right text-[13px] text-foreground-700 transition-colors hover:bg-background-50"
                  >
                    <i className="ri-settings-3-line text-base" aria-hidden="true"></i>
                    الإعدادات
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      setMenuOpen(false);
                      navigate('/auth/login');
                    }}
                    className="flex w-full items-center gap-2 rounded-lg px-3 py-2.5 text-right text-[13px] text-danger-600 transition-colors hover:bg-danger-50"
                  >
                    <i className="ri-logout-box-r-line text-base" aria-hidden="true"></i>
                    تسجيل الخروج
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      </div>
    </header>
  );
}